'use client';

import React, { JSX } from 'react';
import { HomeScreenSectionTypeEnum, ICmsSection, IBanner } from '../services/types';
import { tryParseContent } from './cms.helpers';
import Container from '@/src/components/ui/container';
import ImagesCarousel from '@/src/components/carousel/ImagesCarousel';
import Banner from './Banner';
import DigitalCard from '@/src/app/[locale]/digital-cards/components/DigitalCardLink';
import { PromotedProducts } from './PromotedProducts';
import { PromotedProductLayout } from './PromotedProductLayout';
import { PromotedStore } from './PromotedStore';
import { PromoBanner } from './PromoBanner';
import { HeroBanner } from './HeroBanner';
import { NewTrendingStores } from './NewTrendingStores';
import { NearbyStores } from './NearbyStores';
import { PromotedStores } from './PromotedStores';
import { Department } from './Department';
import { AllProducts } from './AllProducts';

interface CmsSectionRendererProps {
  section: ICmsSection;
  allProductsPageId?: string;
  globalLiveTimes?: Record<number, string>;
  branchId?: number;
}

export const CmsSectionRenderer = ({
  section,
  allProductsPageId,
  globalLiveTimes,
  branchId,
}: CmsSectionRendererProps): JSX.Element | null => {
  const { parsed: parsedContent, isValid } = tryParseContent(section.content, section.sectionsType);

  if (!isValid) return null;

  switch (section.sectionsType) {
    case HomeScreenSectionTypeEnum.FourImages:
      return (
        <Container>
          <ImagesCarousel
            description={section.description}
            name={section.name}
            showingSectionDescription={section.showSectionDescription}
            showingSectionTitle={section.showSectionTitle}
            numberOfImages="fourImages"
            sliders={parsedContent}
            branchId={branchId}
          />
        </Container>
      );

    case HomeScreenSectionTypeEnum.TwoImages:
      return (
        <Container>
          <ImagesCarousel
            description={section.description}
            name={section.name}
            showingSectionDescription={section.showSectionDescription}
            showingSectionTitle={section.showSectionTitle}
            numberOfImages="twoImages"
            sliders={parsedContent}
            branchId={branchId}
          />
        </Container>
      );

    case HomeScreenSectionTypeEnum.ThreeImages:
      return (
        <Container>
          <ImagesCarousel
            description={section.description}
            name={section.name}
            showingSectionDescription={section.showSectionDescription}
            showingSectionTitle={section.showSectionTitle}
            numberOfImages="threeImages"
            sliders={parsedContent}
            branchId={branchId}
          />
        </Container>
      );

    case HomeScreenSectionTypeEnum.OneImage:
      return (
        <Container>
          <ImagesCarousel
            description={section.description}
            name={section.name}
            showingSectionDescription={section.showSectionDescription}
            showingSectionTitle={section.showSectionTitle}
            numberOfImages="oneImage"
            sliders={parsedContent}
            branchId={branchId}
          />
        </Container>
      );

    case HomeScreenSectionTypeEnum.Slider:
      return <Banner slides={parsedContent as IBanner[]} branchId={branchId} />;

    case HomeScreenSectionTypeEnum.PromotedStores:
      return <PromotedStores theme={section.theme} name={section.name} stores={parsedContent} />;

    case HomeScreenSectionTypeEnum.NearbyList:
      return (
        <NearbyStores
          theme={section.theme}
          name={section.name}
          stores={parsedContent}
          globalLiveTimes={globalLiveTimes}
        />
      );

    case HomeScreenSectionTypeEnum.PromotedStore:
      return (
        <PromotedStore
          data={parsedContent}
          theme={section.theme}
          name={section.name}
          globalLiveTimes={globalLiveTimes}
        />
      );

    case HomeScreenSectionTypeEnum.TrendingStores:
      return <NewTrendingStores section={section} />;

    case HomeScreenSectionTypeEnum.DigitalCard:
      return <DigitalCard data={section} />;

    case HomeScreenSectionTypeEnum.PromotedProducts:
    case HomeScreenSectionTypeEnum.TrendingProducts:
      return <PromotedProducts data={section} globalLiveTimes={globalLiveTimes} />;

    case HomeScreenSectionTypeEnum.PromotedProduct:
      return <PromotedProductLayout data={section} globalLiveTimes={globalLiveTimes} />;

    case HomeScreenSectionTypeEnum.PromoBanner:
      return <PromoBanner data={parsedContent} name={section.name} branchId={branchId} />;

    case HomeScreenSectionTypeEnum.HeroBanner:
      return <HeroBanner data={parsedContent} branchId={branchId} />;

    case HomeScreenSectionTypeEnum.Grid:
      return <Department data={parsedContent} branchId={branchId} />;

    case HomeScreenSectionTypeEnum.AllProducts:
      return allProductsPageId ? <AllProducts pageId={allProductsPageId} /> : null;

    default:
      return null;
  }
};
